import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Box from "@mui/system/Box";
import { CaretRight } from "@procore/core-icons";
import { Typography } from "@procore/core-react";
import {
    ElementType,
    FocusEvent,
    forwardRef,
    HTMLAttributes,
    MouseEvent,
    ReactNode,
    Ref,
    RefAttributes,
    RefObject,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

const StyledMenuItem = styled(MenuItem)({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingTop: "2px",
    paddingBottom: "2px",
});

type IconMenuItemProps = {
    MenuItemProps?: MenuItemProps;
    className?: string;
    disabled?: boolean;
    label?: string;
    renderLabel?: () => React.ReactNode;
    leftIcon?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
    ref?: RefObject<HTMLLIElement>;
    rightIcon?: React.ReactNode;
};

const FlexBox = styled(Box)({
    display: "flex",
});

const IconMenuItem = forwardRef<HTMLLIElement, IconMenuItemProps>(function IconMenuItem(
    { MenuItemProps, className, label, leftIcon, renderLabel, rightIcon, ...props },
    ref,
) {
    return (
        <StyledMenuItem {...MenuItemProps} ref={ref} className={className} {...props}>
            <FlexBox>
                {leftIcon}
                {renderLabel ? (
                    renderLabel()
                ) : (
                    <Typography style={{ paddingLeft: "4px" }} intent="body">
                        {label}
                    </Typography>
                )}
            </FlexBox>
            {rightIcon}
        </StyledMenuItem>
    );
});

export type NestedMenuItemProps = Omit<MenuItemProps, "button"> & {
    parentMenuOpen: boolean;
    component?: ElementType;
    label?: string;
    renderLabel?: () => ReactNode;
    rightIcon?: ReactNode;
    leftIcon?: ReactNode;
    children?: ReactNode;
    className?: string;
    tabIndex?: number;
    disabled?: boolean;
    ContainerProps?: HTMLAttributes<HTMLElement> & RefAttributes<HTMLElement | null>;
    MenuProps?: Partial<Omit<MenuProps, "children">>;
    button?: true | undefined;
    delay?: number;
};

const NestedMenuItem = forwardRef<HTMLLIElement | null, NestedMenuItemProps>(function NestedMenuItem(props, ref) {
    const {
        parentMenuOpen,
        label,
        renderLabel,
        rightIcon = <CaretRight />,
        leftIcon = null,
        children,
        className,
        tabIndex: tabIndexProp,
        ContainerProps: ContainerPropsProp = {},
        MenuProps,
        delay = 0,
        ...MenuItemProps
    } = props;

    const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

    const menuItemRef = useRef<HTMLLIElement | null>(null);
    useImperativeHandle(ref, () => menuItemRef.current!);

    const containerRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(containerRefProp as Ref<HTMLElement | null>, () => containerRef.current);

    const menuContainerRef = useRef<HTMLDivElement | null>(null);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const handleMouseEnter = (e: MouseEvent<HTMLElement>) => {
        timeoutRef.current = setTimeout(() => {
            if (!props.disabled) {
                setIsSubMenuOpen(true);
            }

            if (ContainerProps.onMouseEnter) {
                ContainerProps.onMouseEnter(e);
            }
        }, delay);
    };

    const handleMouseLeave = (e: MouseEvent<HTMLElement>) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        setIsSubMenuOpen(false);

        if (ContainerProps.onMouseLeave) {
            ContainerProps.onMouseLeave(e);
        }
    };

    const handleFocus = (e: FocusEvent<HTMLElement>) => {
        if (e.target === containerRef.current && !props.disabled) {
            setIsSubMenuOpen(true);
        }

        if (ContainerProps.onFocus) {
            ContainerProps.onFocus(e);
        }
    };

    const open = isSubMenuOpen && parentMenuOpen;

    // Root element must have a `tabIndex` attribute for keyboard navigation
    let tabIndex;
    if (!props.disabled) {
        tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
    }

    return (
        <div
            {...ContainerProps}
            ref={containerRef}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <IconMenuItem
                MenuItemProps={MenuItemProps}
                className={className}
                ref={menuItemRef}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                label={label}
                renderLabel={renderLabel}
            />

            <Menu
                // Set pointer events to 'none' to prevent the invisible Popover div
                // from capturing events for clicks and hovers
                style={{ pointerEvents: "none", marginTop: "-8px" }}
                anchorEl={menuItemRef.current}
                anchorOrigin={{
                    horizontal: "right",
                    vertical: "top",
                }}
                transformOrigin={{
                    horizontal: "left",
                    vertical: "top",
                }}
                open={open}
                autoFocus={false}
                disableEnforceFocus
                transitionDuration={0}
                onClose={() => {
                    setIsSubMenuOpen(false);
                }}
                {...MenuProps}
            >
                <div ref={menuContainerRef} style={{ pointerEvents: "auto" }}>
                    {children}
                </div>
            </Menu>
        </div>
    );
});

NestedMenuItem.displayName = "NestedMenuItem";
export { NestedMenuItem };
